import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { UserCategory } from '../../models/user';
import { Page } from '../../models/page';
import { Order, OrderFilters } from '../../models/order';
import { Stock } from '../../models/stock';
import { environment } from '../../../../environments/environment';
import { UpdateStockDto } from '../../models/update-stock-dto';
import { toCamelCaseFromSnakeCase } from '../../util';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private static readonly baseurl = environment.baseurl;

  constructor(private readonly httpClient: HttpClient) {}

  uploadPromotionImage(category: UserCategory, index: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append('index', index.toString());

    return this.httpClient.post<string>(
      `${HttpService.baseurl}/api/files/promotional/img`,
      formData,
      {
        responseType: 'text' as 'json',
      },
    );
  }

  getPromotionalImage(category: UserCategory, index: number) {
    return this.httpClient.get(
      `${HttpService.baseurl}/api/files/promotional/img/${category}/${index}`,
      {
        responseType: 'blob',
      },
    );
  }

  // noinspection JSUnusedGlobalSymbols
  uploadStockFile(
    file: File,
    meta: {
      JAPAN: number;
      NON_JAPAN: number;
      INDIAN: number;
      ELECTRICAL: number;
      BEARING: number;
    },
  ) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('japan', meta.JAPAN.toString());
    formData.append('nonJapan', meta.NON_JAPAN.toString());
    formData.append('indian', meta.INDIAN.toString());
    formData.append('electrical', meta.ELECTRICAL.toString());
    formData.append('bearing', meta.BEARING.toString());

    return this.httpClient.post<string>(`${HttpService.baseurl}/api/files/stock`, formData, {
      responseType: 'text' as 'json',
    });
  }

  uploadStockFileV2(data: Partial<Record<UserCategory, UpdateStockDto>>) {
    const formData = new FormData();
    for (const category of Object.entries(data)) {
      let [key, value] = category;
      key = toCamelCaseFromSnakeCase(key as UserCategory);
      formData.append(`${key}.file`, value.file);
      formData.append(`${key}.start`, value.start.toString());
      formData.append(`${key}.end`, value.end.toString());
    }

    return this.httpClient.post<string>(`${HttpService.baseurl}/api/v2/files/stock`, formData, {
      responseType: 'text' as 'json',
    });
  }

  getOrders(query: OrderFilters & { page: number; size: number }) {
    const { page, size, ...filters } = query;
    let params = new HttpParams().set('page', page).set('size', size);

    // see OrderFilters: a blank enum is a 400, not a wildcard
    for (const [key, value] of Object.entries(filters)) {
      if (value === '' || value === null || value === undefined) continue;
      params = params.set(key, value as string | number);
    }

    return this.httpClient.get<Page<Order>>(`${HttpService.baseurl}/api/orders`, { params });
  }

  getStockById(stockId: number) {
    return this.httpClient.get<Stock>(`${HttpService.baseurl}/api/stocks/${stockId}`);
  }

  updateOrder(args: { id: string; status: 'APPROVED' | 'REJECTED' }) {
    if (args.status === 'APPROVED') {
      return this.httpClient.patch<Order>(
        `${HttpService.baseurl}/api/orders/approve/${args.id}`,
        null,
      );
    } else {
      return this.httpClient.patch<Order>(
        `${HttpService.baseurl}/api/orders/reject/${args.id}`,
        null,
      );
    }
  }
}
